import { Mine__factory } from 'public/abi/evm/types';
import { ERRORS_CONFIG, Tokens } from '~/utils/constants';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import type { TwoTokenLicense } from '~/types/licenses';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';

export default () => {
  const store = useMainStore();
  const { blockchain } = useEnvs();
  const { address } = useWeb3ModalAccount();
  const { minedBalance } = useMinedBalance();
  const { getProvider } = useProvider();

  const totalMinedAmount = reactive<TwoTokenLicense>({ primaryToken: '0', secondaryToken: '0' });
  const isLoading = ref(false);
  const primaryTokenAmounts = ref<number[]>([]);
  const secondaryTokenAmounts = ref<number[]>([]);
  const secondaryTokenBalance = ref<number>(0);

  let reqId = 0;
  let aborted = false;

  async function getTotalMinedAmount() {
    const myId = ++reqId;
    isLoading.value = true;

    try {
      const tokenSnap = store.currentToken;
      const addrSnap = address.value;
      if (tokenSnap === Tokens.pearl || !addrSnap) {
        if (myId === reqId) {
          totalMinedAmount.primaryToken = '0';
          totalMinedAmount.secondaryToken = '0';
          primaryTokenAmounts.value = [];
          secondaryTokenAmounts.value = [];
        }
        return;
      }

      const mineAddress = blockchain.contracts[tokenSnap].addresses.mine;
      if (!mineAddress) throw new Error('No mine address found');

      const mineContract = Mine__factory.connect(mineAddress, getProvider());

      const licenses = await mineContract.getLicenses(addrSnap, 0, 255);
      const secBal = await mineContract
        .secondaryTokenBalance(addrSnap)
        .then((res) => BigNumber(formatEther(res)).toNumber());

      const currentLicenseIndex = BigNumber(licenses?.[2]?.toString()).toNumber() || 0;

      const primArr =
        !licenses || licenses[0].length === 0 || BigNumber(licenses[0]?.[0]?.toString()).isNaN()
          ? []
          : licenses[0].map((i: bigint) => parseInt(i.toString()));
      const secArr =
        !licenses || licenses[1].length === 0 || BigNumber(licenses[1]?.[0]?.toString()).isNaN()
          ? []
          : licenses[1].map((i: bigint) => parseInt(i.toString()));

      if (currentLicenseIndex < 0 || currentLicenseIndex > primArr.length || currentLicenseIndex > secArr.length) {
        throw new Error('Invalid currentLicenseIndex');
      }

      const primaryTokenSummedAmounts = primArr.reduce(
        (sum, current) => BigNumber(sum).plus(new BigNumber(current).shiftedBy(-18)).toNumber(),
        0
      );
      const secondaryTokenSummedAmounts = secArr.reduce(
        (sum, current) => BigNumber(sum).plus(new BigNumber(current).shiftedBy(-18)).toNumber(),
        0
      );

      const nextPrimary = BigNumber(primaryTokenSummedAmounts).toString();
      const nextSecondary = BigNumber(secondaryTokenSummedAmounts).toString();

      if (!aborted && myId === reqId && tokenSnap === store.currentToken && addrSnap === address.value) {
        secondaryTokenBalance.value = secBal;
        primaryTokenAmounts.value = primArr;
        secondaryTokenAmounts.value = secArr;
        totalMinedAmount.primaryToken = nextPrimary;
        totalMinedAmount.secondaryToken = nextSecondary;
      }
    } catch (e) {
      if (!aborted && myId === reqId) {
        console.error(e);
        console.log(ERRORS_CONFIG.blockchainErrors.totalProduce);
      }
    } finally {
      if (myId === reqId) isLoading.value = false;
    }
  }

  // Recompute when true drivers change
  watch(
    [() => store.currentToken, () => address.value, () => minedBalance.value],
    () => {
      getTotalMinedAmount();
    },
    { immediate: true, deep: true }
  );

  onUnmounted(() => {
    aborted = true;
    reqId++;
  });

  return {
    isLoading,
    getTotalMinedAmount,
    totalMinedAmount,
    secondaryTokenAmounts,
    primaryTokenAmounts,
    secondaryTokenBalance
  };
};
